import {
  Box,
  Button,
  ButtonGroup,
  MenuItem,
  IconButton,
  Stack,
  TextField,
  Typography,
  DialogContent,
  Dialog,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useEffect, JSX } from "react";
import { Transaction } from "../types";
import { transactionSchema, validationSchema } from "../validations/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { setSelectedTransaction } from "../store/appSlice";
import { useTransaction } from "../hooks/useTransaction";
import { renderCategoryIcon } from "./common/Category";
import { useTranslation } from "react-i18next";

interface TransactionFormProps {
  isEntryDrawerOpen: boolean;
  onCloseForm: () => void;
  isInputFormDialog: boolean;
  onInputFormDialog: () => void;
}

const TransactionForm = ({
  isEntryDrawerOpen,
  onCloseForm,
  isInputFormDialog,
  onInputFormDialog,
}: TransactionFormProps) => {
  const { isMobile, currentDay, selectedTransaction, isLoggedIn, category } =
    useSelector((state: RootState) => state.app);
  const dispatch = useDispatch();
  const { onSaveTransaction, onUpdateTransaction, onDeleteTransaction } =
    useTransaction();
  const { t: tCommon } = useTranslation("common");
  const { t: tError } = useTranslation("error");

  // console.log("Form:selectedTransaction:", selectedTransaction);
  const formWidth = 320;

  const {
    control,
    setValue,
    watch,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<validationSchema>({
    // 初期値
    defaultValues: {
      type: "expense",
      date: currentDay,
      category: "",
      amount: 0,
      store: "",
      content: "",
    },
    resolver: zodResolver(transactionSchema),
  });
  // console.log("--s0100--errors:", errors);

  const incomeExpenseToggle = (type: "income" | "expense") => {
    // console.log("clicked button", type);
    setValue("type", type);
    reset({
      type,
      date: currentDay,
      amount: 0,
      category: "",
      store: "",
      content: "",
    });
    dispatch(setSelectedTransaction(null));
  };

  // 収支の切替を監視して保存ボタンのをを変える
  const inputFormSelectedType = watch("type");

  useEffect(() => {
    setValue("date", currentDay);
    if (selectedTransaction?.id) {
      reset({
        type: selectedTransaction.type,
        date: selectedTransaction.date,
        category: selectedTransaction.category,
        amount: selectedTransaction.amount,
        store: selectedTransaction.store,
        content: selectedTransaction.content,
      });
    } else {
      reset({
        type: "expense",
        date: currentDay,
        amount: 0,
        category: "",
        store: "",
        content: "",
      });
    }
  }, [setValue, currentDay, selectedTransaction]);

  // react hook formの推奨書き方
  const onSubmit: SubmitHandler<validationSchema> = (
    data: validationSchema,
  ) => {
    if (!isLoggedIn) {
      alert(tError("warn.NotLoggedIn"));
      return;
    }
    // console.log("onSubmit:data:", data);
    if (selectedTransaction) {
      onUpdateTransaction({
        id: selectedTransaction.id,
        ...data,
      } as Transaction)
        .then(() => {
          dispatch(setSelectedTransaction(null));
        })
        .catch((error) => {
          console.error("更新に失敗しました", error);
          alert(`${tCommon("error.update")} ${error.message}`);
        });
    } else {
      onSaveTransaction(data as Transaction)
        .then(() => {
          console.log("取引保存。");
        })
        .catch((error) => {
          console.error("取引保存に失敗しました", error);
          alert(`${tCommon("error.save")} ${error.message}`);
        });

      // 保存後フォームを初期化、ただし日付は選択日にする
      reset({
        type: "expense",
        date: currentDay,
        amount: 0,
        category: "",
        store: "",
        content: "",
      });
    }
  };

  const InputFormContent: React.ReactElement = (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography>{tCommon("transaction.add")}</Typography>
        {/* 閉じるボータン */}
        <IconButton onClick={onCloseForm}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* form */}
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          {/* 収支切替 */}
          <Controller
            name="type"
            control={control}
            render={({ field }) => {
              // console.log("react-hook-form:type:field:", field);
              return (
                <ButtonGroup fullWidth>
                  <Button
                    variant={
                      field.value === "expense" ? "contained" : "outlined"
                    }
                    color="error"
                    onClick={() => incomeExpenseToggle("expense")}
                  >
                    {tCommon("transaction.expense")}
                  </Button>
                  <Button
                    variant={
                      field.value === "income" ? "contained" : "outlined"
                    }
                    color="primary"
                    onClick={() => incomeExpenseToggle("income")}
                  >
                    {tCommon("transaction.income")}
                  </Button>
                </ButtonGroup>
              );
            }}
          />

          <Controller
            name="date"
            control={control}
            render={({ field }) => {
              // console.log("react-hook-form:field:", field);
              return (
                <TextField
                  {...field}
                  label={tCommon("transaction.date")}
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.date}
                  helperText={
                    errors.date?.message && <>{tError(errors.date.message)}</>
                  }
                />
              );
            }}
          />
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={tCommon("transaction.category")}
                id={tCommon("transaction.category")}
                select
                error={!!errors.category}
                helperText={
                  errors.category?.message && (
                    <>{tError(errors.category.message)}</>
                  )
                }
              >
                {inputFormSelectedType === "expense"
                  ? category?.expense.map((c) => (
                      <MenuItem value={c.label}>
                        {renderCategoryIcon(c.label, c.icon)}
                        {c.label}
                      </MenuItem>
                    ))
                  : category?.income.map((c) => (
                      <MenuItem value={c.label}>
                        {renderCategoryIcon(c.label, c.icon)}
                        {c.label}
                      </MenuItem>
                    ))}
              </TextField>
            )}
          />
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value === 0 ? "" : field.value}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value) || 0;
                  field.onChange(newValue);
                }}
                label={tCommon("transaction.amount")}
                type="number"
                error={!!errors.amount}
                helperText={
                  errors.amount?.message && <>{tError(errors.amount.message)}</>
                }
              />
            )}
          />
          <Controller
            name="store"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  inputFormSelectedType === "income"
                    ? tCommon("transaction.store")
                    : tCommon("transaction.store")
                }
                type="text"
                error={!!errors.store}
                helperText={
                  errors.store?.message && <>{tError(errors.store.message)}</>
                }
              />
            )}
          />
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={tCommon("transaction.content")}
                type="text"
                error={!!errors.content}
                helperText={
                  errors.content?.message && (
                    <>{tError(errors.content?.message)}</>
                  )
                }
              />
            )}
          />
          {/* 保存ボタン */}
          <Button
            type="submit"
            variant="contained"
            color={inputFormSelectedType === "income" ? "primary" : "error"}
            fullWidth
          >
            {selectedTransaction
              ? tCommon("transaction.update")
              : tCommon("actions.save")}
          </Button>
          {selectedTransaction ? (
            <>
              {/* 削除ボタン */}
              <Button
                // type="submit"
                variant="outlined"
                color={inputFormSelectedType === "income" ? "primary" : "error"}
                fullWidth
                onClick={() => onDeleteTransaction(selectedTransaction)}
              >
                {tCommon("transaction.delete")}
              </Button>
            </>
          ) : null}
        </Stack>
      </Box>
    </>
  );

  return isMobile ? (
    <Dialog
      open={isInputFormDialog}
      onClose={onInputFormDialog}
      fullWidth
      maxWidth={"sm"}
    >
      <DialogContent>{InputFormContent}</DialogContent>
    </Dialog>
  ) : (
    <Box
      sx={{
        position: "fixed",
        top: 64,
        right: isEntryDrawerOpen ? formWidth : 0,
        width: formWidth,
        height: "100vh",
        bgcolor: "background.paper",
        zIndex: 900,
        transition: (theme) =>
          theme.transitions.create("right", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        p: 2,
        boxSizing: "border-box",
        boxShadow: "0 0 1 15px -5px ##999999",
        border: "1px solid #e0e0e0e0",
      }}
    >
      {InputFormContent}
    </Box>
  );
};

export default TransactionForm;
